import { useCallback, useEffect, useState } from 'react';
import { DocumentType } from '../shared/types';
import { apiDelete, apiGet, apiPost } from '../utils/api';
import { useCurrentOrganization } from '../contexts/OrganizationContext';

type DocumentTypeCategory = 'clinical-note' | 'document';

export const useDocumentTypes = (category: DocumentTypeCategory) => {
  const organization = useCurrentOrganization();
  const [ error, setError ] = useState<string | null>(null);

  const fetchDocumentTypes = useCallback(async (): Promise<DocumentType[]> => {
    if (!organization) {
      setError('Organization not found');
      return [];
    }

    try {
      const response = await apiGet(`/api/organizations/${organization.id}/document-types`, { category });
      const data = await response.json();

      if (!data.error) {
        return data;
      } else {
        setError(data.error);
        return [];
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to fetch document types');
      return [];
    }
  }, [organization, category]);

  const addDocumentType = async (txa_id: string, label: string): Promise<void> => {
    if (!organization) {
      setError('Organization not found');
      return;
    }

    try {
      const response = await apiPost(`/api/organizations/${organization.id}/document-types`, { txa_id, label, category });
      const data = await response.json();
      if (data.error) {
        setError(data.error);
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to add document type');
    }
  };

  const deleteDocumentType = async (id: string): Promise<void> => {
    if (!organization) {
      setError('Organization not found');
      return;
    }

    try {
      const response = await apiDelete(`/api/organizations/${organization.id}/document-types`, { id, category });
      const data = await response.json();
      if (data.error) {
        setError(data.error);
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to delete document type');
    }
  };

  useEffect(() => {
    if (error) {
      setTimeout(() => {
        setError(null);
      }, 5000);
    }
  }, [ error ]);

  return { fetchDocumentTypes, addDocumentType, deleteDocumentType, error };
};
